import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Shuffle, BookOpen, Globe, FlaskConical, Dumbbell, Palette, Moon, Languages, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryIcons = {
  "تاريخ": BookOpen,
  "جغرافيا": Globe,
  "علوم": FlaskConical,
  "رياضة": Dumbbell,
  "فن وثقافة": Palette,
  "دين": Moon,
  "لغة عربية": Languages,
  "معلومات عامة": HelpCircle,
};

const categoryColors = {
  "تاريخ": "from-amber-600 to-amber-800",
  "جغرافيا": "from-emerald-600 to-emerald-800",
  "علوم": "from-blue-600 to-blue-800",
  "رياضة": "from-red-600 to-red-800",
  "فن وثقافة": "from-purple-600 to-purple-800",
  "دين": "from-teal-600 to-teal-800",
  "لغة عربية": "from-orange-600 to-orange-800",
  "معلومات عامة": "from-gray-600 to-gray-800",
};

export default function CategoryPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      // Use default categories
      setCategories([
        "تاريخ", "جغرافيا", "علوم", "رياضة",
        "فن وثقافة", "دين", "لغة عربية", "معلومات عامة"
      ]);
    }
  };

  const startGame = async (category = null) => {
    setIsLoading(true);
    setSelectedCategory(category);
    
    try {
      const response = await axios.post(`${API}/game/start`, null, {
        params: category ? { category } : {}
      });
      
      toast.success("تم بدء اللعبة بنجاح!");
      navigate(`/game/${response.data.session_id}`);
    } catch (error) {
      console.error("Error starting game:", error);
      
      if (error.response?.status === 400) {
        toast.error("لا توجد أسئلة كافية لهذه الفئة. جرب فئة أخرى.");
      } else {
        toast.error("حدث خطأ أثناء بدء اللعبة");
      }
    } finally {
      setIsLoading(false);
      setSelectedCategory(null);
    }
  };

  return (
    <div className="studio-background min-h-screen p-4 md:p-8">
      {/* Back Button */}
      <Button
        data-testid="back-button"
        onClick={() => navigate("/")}
        variant="ghost"
        className="absolute top-4 right-4 text-white hover:bg-white/10"
      >
        <ArrowRight className="ml-2 w-5 h-5" />
        رجوع
      </Button>

      <div className="max-w-4xl mx-auto pt-16">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-3xl md:text-4xl text-white mb-4">
            اختر الفئة
          </h1>
          <p className="font-body text-white/70 text-lg">
            اختر فئة محددة أو العب بأسئلة عشوائية من جميع الفئات
          </p>
        </div>

        {/* Random Category Button */}
        <div className="mb-8 animate-fade-in-up">
          <Button
            data-testid="random-category-button"
            onClick={() => startGame(null)}
            disabled={isLoading}
            className="w-full py-6 text-xl font-heading bg-gradient-to-r from-neon-cyan to-neon-cyan-dim hover:from-neon-cyan-dim hover:to-neon-cyan text-studio border-2 border-white/50 rounded-2xl shadow-neon-cyan transition-all duration-300 hover:scale-[1.02]"
          >
            <Shuffle className="ml-3 w-6 h-6" />
            {isLoading && selectedCategory === null ? "جاري التحميل..." : "أسئلة عشوائية من جميع الفئات"}
          </Button>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-8">
          <div className="flex-1 h-px bg-white/20"></div>
          <span className="text-white/50 font-body">أو اختر فئة</span>
          <div className="flex-1 h-px bg-white/20"></div>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((category, index) => {
            const IconComponent = categoryIcons[category] || HelpCircle;
            const colorClass = categoryColors[category] || "from-gray-600 to-gray-800";
            
            return (
              <button
                key={category}
                data-testid={`category-${category}`}
                onClick={() => startGame(category)}
                disabled={isLoading}
                className={`
                  group relative p-6 rounded-xl bg-gradient-to-br ${colorClass}
                  border-2 border-white/30 hover:border-white/60
                  transition-all duration-300 hover:scale-105 hover:shadow-lg
                  disabled:opacity-50 disabled:cursor-not-allowed
                  animate-fade-in-up
                `}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <span className="font-body text-white text-sm md:text-base font-medium text-center">
                    {category}
                  </span>
                </div>
                
                {isLoading && selectedCategory === category && (
                  <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
                    <div className="loading-spinner w-8 h-8"></div>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Info */}
        <div className="mt-12 text-center">
          <p className="text-white/50 font-body text-sm">
            كل لعبة تحتوي على 15 سؤال بصعوبة تصاعدية
          </p>
          <p className="text-neon-gold/70 font-body text-sm mt-2">
            نقاط الأمان: السؤال 5 والسؤال 10
          </p>
        </div>
      </div>
    </div>
  );
}
